import {
  createBaseThemeOptions,
  createUnifiedTheme,
  defaultTypography,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

export const enlivioColors = {
  midnightGreen: '#001817',
  vibrantGreen: '#D9FF05',
  offWhite: '#FCFCFC',
  coolGrey1: '#EFEFEF',
  coolGrey2: '#C9C6C9',
  coolGrey3: '#B0B0B0',
  warmGrey: '#E0DBC7',
};

const {
  midnightGreen,
  vibrantGreen,
  offWhite,
  coolGrey1,
  coolGrey2,
  coolGrey3,
  warmGrey,
} = enlivioColors;
const enlivioTypography = {
  ...defaultTypography,
  fontFamily: "'Geist', Arial, sans-serif",
  h1: { ...defaultTypography.h1, fontWeight: 400 },
  h2: { ...defaultTypography.h2, fontWeight: 400 },
  h3: { ...defaultTypography.h3, fontWeight: 500 },
  h4: { ...defaultTypography.h4, fontWeight: 500 },
  h5: { ...defaultTypography.h5, fontWeight: 500 },
  h6: { ...defaultTypography.h6, fontWeight: 500 },
};

export const customLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: midnightGreen,
        light: midnightGreen,
        dark: midnightGreen,
        contrastText: offWhite,
      },
      background: {
        default: offWhite,
        paper: coolGrey1,
      },
      text: {
        primary: midnightGreen,
        secondary: midnightGreen,
      },
      divider: coolGrey2,
      action: {
        disabled: coolGrey3,
      },
      error: {
        main: midnightGreen,
        light: midnightGreen,
        dark: midnightGreen,
        contrastText: offWhite,
      },
      info: {
        main: coolGrey3,
        light: coolGrey3,
        dark: coolGrey3,
        contrastText: midnightGreen,
      },
      success: {
        main: midnightGreen,
        light: midnightGreen,
        dark: midnightGreen,
        contrastText: offWhite,
      },
      warning: {
        main: warmGrey,
        light: warmGrey,
        dark: warmGrey,
        contrastText: midnightGreen,
      },
      navigation: {
        background: midnightGreen,
        indicator: vibrantGreen,
        color: offWhite,
        selectedColor: offWhite,
        navItem: {
          hoverBackground: midnightGreen,
        },
      },
    },
    typography: enlivioTypography,
  }),
  pageTheme: {
    home: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
  },
  defaultPageTheme: 'home',
  fontFamily: "'Geist', Arial, sans-serif",
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 0,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          backgroundImage: 'unset',
          backgroundColor: midnightGreen,
        }),
        title: () => ({
          color: offWhite,
          fontWeight: 500,
        }),
        subtitle: () => ({
          color: offWhite,
        }),
        type: () => ({
          color: offWhite,
        }),
      },
    },
  },
});

export const customDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      primary: {
        main: vibrantGreen,
        light: vibrantGreen,
        dark: vibrantGreen,
        contrastText: midnightGreen,
      },
      background: {
        default: midnightGreen,
        paper: midnightGreen,
      },
      text: {
        primary: offWhite,
        secondary: coolGrey1,
      },
      divider: coolGrey2,
      action: {
        disabled: coolGrey3,
      },
      error: {
        main: coolGrey2,
        light: coolGrey2,
        dark: coolGrey2,
        contrastText: midnightGreen,
      },
      info: {
        main: coolGrey1,
        light: coolGrey1,
        dark: coolGrey1,
        contrastText: midnightGreen,
      },
      success: {
        main: vibrantGreen,
        light: vibrantGreen,
        dark: vibrantGreen,
        contrastText: midnightGreen,
      },
      warning: {
        main: warmGrey,
        light: warmGrey,
        dark: warmGrey,
        contrastText: midnightGreen,
      },
      navigation: {
        background: midnightGreen,
        indicator: vibrantGreen,
        color: offWhite,
        selectedColor: offWhite,
        navItem: {
          hoverBackground: midnightGreen,
        },
      },
    },
    typography: enlivioTypography,
  }),
  pageTheme: {
    home: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [midnightGreen, midnightGreen],
      shape: shapes.wave,
    }),
  },
  defaultPageTheme: 'home',
  fontFamily: "'Geist', Arial, sans-serif",
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 0,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          backgroundImage: 'unset',
          backgroundColor: midnightGreen,
        }),
        title: () => ({
          color: offWhite,
          fontWeight: 500,
        }),
        subtitle: () => ({
          color: offWhite,
        }),
        type: () => ({
          color: offWhite,
        }),
      },
    },
  },
});
